/* eslint-disable @typescript-eslint/no-explicit-any */
import React from 'react';
import { Box, HStack, Icon, Image, Text } from '@chakra-ui/react';
import { motion } from 'framer-motion';
import { FaMedal } from 'react-icons/fa6';
import { Product } from '../interfaces';
import { images } from './utils';

const planSimulators = {
  bronze: {
    rank: 1,
    name: 'Bronze',
    color: 'orange.500',
    icon: FaMedal,
  },
  prata: {
    rank: 2,
    name: 'Prata',
    color: 'gray.500',
    icon: FaMedal,
  },
  ouro: {
    rank: 3,
    name: 'Ouro',
    color: 'yellow.500',
    icon: FaMedal,
  },
  safira: {
    rank: 4,
    name: 'Safira',
    color: 'cyan.500',
    icon: FaMedal,
  },
  diamante: {
    rank: 5,
    name: 'Diamante',
    color: 'purple.500',
    icon: FaMedal,
  },
} as const;

export type SimulatorPlanKey = keyof typeof planSimulators;

export const SIMULATOR_LEVELS = [
  { key: 'bronze' as SimulatorPlanKey, rank: planSimulators.bronze.rank },
  { key: 'prata' as SimulatorPlanKey, rank: planSimulators.prata.rank },
  { key: 'ouro' as SimulatorPlanKey, rank: planSimulators.ouro.rank },
  { key: 'safira' as SimulatorPlanKey, rank: planSimulators.safira.rank },
  { key: 'diamante' as SimulatorPlanKey, rank: planSimulators.diamante.rank },
] as const;

const frameDimensions = {
  default: {
    deviceWidth: 219,
    deviceHeight: 450,
    minContainerWidth: '219px',
  },
  compact: {
    deviceWidth: 180,
    deviceHeight: 360,
    minContainerWidth: '180px',
  },
};

type FrameVariant = keyof typeof frameDimensions;

interface SmartphoneFrameProps {
  plan: SimulatorPlanKey;
  products?: Product[];
  variant?: FrameVariant;
}

export const SmartphoneFrame: React.FC<SmartphoneFrameProps> = ({
  plan,
  products = [],
  variant = 'default',
}) => {
  const planData = planSimulators[plan];
  const dimensions = frameDimensions[variant];

  console.log('products', products);
  return (
    <Box
      bg="white"
      p={variant === 'compact' ? 3 : 4}
      borderRadius="lg"
      boxShadow="md"
      border="1px solid"
      borderColor="gray.200"
      textAlign="center"
      width="100%"
    >
      <HStack justify="center" spacing={2} mb={4}>
        <Icon as={planData.icon} color={planData.color} boxSize={5} />
        <Text fontSize="lg" fontWeight="bold" color={planData.color}>
          {planData.name}
        </Text>
      </HStack>

      <Box
        width="100%"
        height="100%"
        display="flex"
        justifyContent="center"
        alignItems="center"
        overflow="hidden"
      >
        <Box
          position="relative"
          minWidth={dimensions.minContainerWidth}
          maxWidth={`${dimensions.deviceWidth}px`}
          minHeight={`${dimensions.deviceHeight}px`}
          maxHeight={`${dimensions.deviceHeight}px`}
          display="flex"
          justifyContent="center"
          alignItems="center"
        >
          <Image
            position="absolute"
            top={0}
            left={0}
            src="/assets/iphone.png"
            objectFit="contain"
            alt="Moldura do iPhone para preview do app"
            width="100%"
            height="100%"
            zIndex={1}
          />

          <Box
            position="absolute"
            top="12.5%"
            left="50%"
            transform="translateX(-50%)"
            width="90%"
            height="80%"
            overflow="hidden"
            borderRadius={{ base: '15px', md: '20px', lg: '25px' }}
            zIndex={2}
            p={1}
            bg="white"
          >
            <Box
              display="flex"
              flexDirection="column"
              gap={1}
              width="100%"
              height="100%"
              justifyContent="flex-start"
              alignItems="center"
              overflow="hidden"
              pt={2}
            >
              {products.length > 0 ? (
                products
                  .reduce((rows: Array<Array<Product>>, product, index) => {
                    const rowIndex = Math.floor(index / 3);
                    if (!rows[rowIndex]) {
                      rows[rowIndex] = [];
                    }
                    rows[rowIndex].push(product);
                    return rows;
                  }, [])
                  .map((row, rowIndex) => (
                    <motion.div
                      key={rowIndex}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{
                        duration: 0.4,
                        delay: rowIndex * 0.2,
                        type: 'spring',
                        stiffness: 80,
                      }}
                    >
                      <HStack spacing={1} justify="center">
                        {row.map((product, index) => (
                          <motion.div
                            key={`${rowIndex}-${index}`}
                            initial={{ opacity: 0, scale: 0.8 }}
                            animate={{ opacity: 1, scale: 1 }}
                            transition={{
                              duration: 0.3,
                              delay: rowIndex * 0.2 + index * 0.1,
                              type: 'spring',
                              stiffness: 120,
                            }}
                            whileHover={{
                              scale: 1.02,
                              y: -1,
                              transition: { duration: 0.2 },
                            }}
                            whileTap={{ scale: 0.95 }}
                          >
                            <Box
                              display="flex"
                              flexDirection="column"
                              alignItems="center"
                              justifyContent="center"
                              p={1}
                              bg="white"
                              borderRadius="md"
                              boxShadow="0 1px 4px rgba(0, 0, 0, 0.1)"
                              border="1px solid"
                              borderColor="gray.200"
                              width="55px"
                              height="55px"
                              position="relative"
                              _hover={{
                                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
                                borderColor: 'blue.300',
                              }}
                            >
                              <Image
                                src={product.metadata.logo?.value}
                                alt={product.name}
                                width="24px"
                                height="24px"
                                objectFit="contain"
                              />
                              <Text
                                fontSize="9px"
                                textAlign="center"
                                color="gray.700"
                                fontWeight="medium"
                                lineHeight="tight"
                                maxW="48px"
                                noOfLines={1}
                              >
                                {product.name}
                              </Text>
                            </Box>
                          </motion.div>
                        ))}
                      </HStack>
                    </motion.div>
                  ))
              ) : (
                <Box
                  display="flex"
                  flexDirection="column"
                  alignItems="center"
                  justifyContent="center"
                  height="100%"
                  color="gray.500"
                >
                  <Text fontSize="sm" textAlign="center">
                    Nenhum produto disponível para este plano
                  </Text>
                </Box>
              )}
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};
